/**
 * Manual Coordination Checklist
 *
 * Checklist for manually coordinating Transportation and DME.
 * User enters estimated times for each item.
 */

import { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Stack,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { addMinutes, format } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

export interface ManualCoordinationStatus {
  transportation: {
    confirmed: boolean;
    estimatedArrival: string | null;
  };
  dme: {
    confirmed: boolean;
    estimatedDelivery: string | null;
  };
}

interface ManualCoordinationChecklistProps {
  onChange: (status: ManualCoordinationStatus) => void;
  disabled?: boolean;
  patientTimezone: string;
}

export function ManualCoordinationChecklist({
  onChange,
  disabled = false,
  patientTimezone,
}: ManualCoordinationChecklistProps) {
  // Helper function to round up to nearest 15 minutes
  const roundUpToNearest15 = (date: Date): Date => {
    const minutes = date.getMinutes();
    const remainder = minutes % 15;
    const roundedMinutes = remainder === 0 ? minutes : minutes + (15 - remainder);

    const rounded = new Date(date);
    rounded.setMinutes(roundedMinutes);
    rounded.setSeconds(0);
    rounded.setMilliseconds(0);

    // setMinutes handles overflow automatically
    return rounded;
  };

  // Helper function to convert time string in patient timezone to ISO
  const timeToISO = (timeStr: string): string => {
    if (!timeStr) return '';

    // Get current date in patient timezone
    const nowInPatientTz = toZonedTime(new Date(), patientTimezone);

    // Parse the time string (HH:mm format)
    const [hours, minutes] = timeStr.split(':').map(Number);

    // Create date with today's date and the specified time in patient timezone
    const dateInPatientTz = new Date(nowInPatientTz);
    dateInPatientTz.setHours(hours, minutes, 0, 0);

    // Convert to UTC and return ISO string
    return fromZonedTime(dateInPatientTz, patientTimezone).toISOString();
  };

  // Calculate default times in patient timezone - 2 hours ahead, rounded up to nearest 15 min
  const nowInPatientTz = toZonedTime(new Date(), patientTimezone);
  const transportTargetTime = addMinutes(nowInPatientTz, 120); // 2 hours from now
  const dmeTargetTime = addMinutes(nowInPatientTz, 120); // 2 hours from now

  const defaultTransportTime = format(roundUpToNearest15(transportTargetTime), 'HH:mm');
  const defaultDmeTime = format(roundUpToNearest15(dmeTargetTime), 'HH:mm');

  const [transportChecked, setTransportChecked] = useState(false);
  const [dmeChecked, setDmeChecked] = useState(false);
  const [transportTime, setTransportTime] = useState(defaultTransportTime);
  const [dmeTime, setDmeTime] = useState(defaultDmeTime);

  const handleTransportCheck = (checked: boolean) => {
    setTransportChecked(checked);

    onChange({
      transportation: {
        confirmed: checked,
        estimatedArrival: checked ? timeToISO(transportTime) : null,
      },
      dme: {
        confirmed: dmeChecked,
        estimatedDelivery: dmeChecked ? timeToISO(dmeTime) : null,
      },
    });
  };

  const handleDmeCheck = (checked: boolean) => {
    setDmeChecked(checked);

    onChange({
      transportation: {
        confirmed: transportChecked,
        estimatedArrival: transportChecked ? timeToISO(transportTime) : null,
      },
      dme: {
        confirmed: checked,
        estimatedDelivery: checked ? timeToISO(dmeTime) : null,
      },
    });
  };

  const handleTransportTimeChange = (time: string) => {
    setTransportTime(time);

    onChange({
      transportation: {
        confirmed: transportChecked,
        estimatedArrival: transportChecked ? timeToISO(time) : null,
      },
      dme: {
        confirmed: dmeChecked,
        estimatedDelivery: dmeChecked ? timeToISO(dmeTime) : null,
      },
    });
  };

  const handleDmeTimeChange = (time: string) => {
    setDmeTime(time);

    onChange({
      transportation: {
        confirmed: transportChecked,
        estimatedArrival: transportChecked ? timeToISO(transportTime) : null,
      },
      dme: {
        confirmed: dmeChecked,
        estimatedDelivery: dmeChecked ? timeToISO(time) : null,
      },
    });
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Manual Coordination
      </Typography>

      <Stack spacing={2}>
        {/* Transportation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={transportChecked}
                onChange={(e) => handleTransportCheck(e.target.checked)}
                disabled={disabled}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingIcon fontSize="small" />
                <Typography>Transportation Confirmed</Typography>
              </Box>
            }
            sx={{ flex: 1 }}
          />
          <TextField
            label="Estimated Arrival"
            type="time"
            value={transportTime}
            onChange={(e) => handleTransportTimeChange(e.target.value)}
            disabled={disabled}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
        </Box>

        {/* DME */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dmeChecked}
                onChange={(e) => handleDmeCheck(e.target.checked)}
                disabled={disabled}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MedicalServicesIcon fontSize="small" />
                <Typography>DME Confirmed</Typography>
              </Box>
            }
            sx={{ flex: 1 }}
          />
          <TextField
            label="Estimated Delivery"
            type="time"
            value={dmeTime}
            onChange={(e) => handleDmeTimeChange(e.target.value)}
            disabled={disabled}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
        </Box>
      </Stack>
    </Paper>
  );
}
